import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import { cn } from '../../lib/styles';

function TooltipProvider({ delayDuration = 0, ...props }: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return <TooltipPrimitive.Provider data-slot="tooltip-provider" delayDuration={delayDuration} {...props} />;
}

export type TooltipVariant = 'primary' | 'secondary';

export type TooltipProps = React.ComponentProps<typeof TooltipPrimitive.Root> & {
  label?: React.ReactNode;
  withinPortal?: boolean;
  asChild?: boolean;
  disabled?: boolean;
  className?: string;
  arrowClassName?: string;
  triggerClassName?: string;
  variant?: TooltipVariant;
  /** Tooltip that wraps inputs should use asDiv instead of asChild to work properly */
  asDiv?: boolean;
} & Pick<TooltipPrimitive.TooltipContentProps, 'side' | 'sideOffset' | 'alignOffset' | 'align'>;

function Tooltip({
  label,
  withinPortal = false,
  asChild = false,
  asDiv = false,
  children,
  disabled = false,
  side,
  sideOffset,
  align,
  alignOffset,
  className,
  arrowClassName,
  triggerClassName,
  variant = 'primary',
  ...rest
}: TooltipProps) {
  if (disabled) return children;

  const trigger = asDiv ? (
    <TooltipTrigger asChild>
      <div className={triggerClassName}>{children}</div>
    </TooltipTrigger>
  ) : (
    <TooltipTrigger asChild={asChild} className={triggerClassName}>
      {children}
    </TooltipTrigger>
  );

  if (label) {
    return (
      <TooltipProvider>
        <TooltipPrimitive.Root data-slot="tooltip" {...rest}>
          {trigger}
          <TooltipContent
            withinPortal={withinPortal}
            variant={variant}
            className={cn(className)}
            arrowClassName={arrowClassName}
            side={side}
            sideOffset={sideOffset}
            align={align}
            alignOffset={alignOffset}
            collisionPadding={16}
          >
            {label}
          </TooltipContent>
        </TooltipPrimitive.Root>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <TooltipPrimitive.Root data-slot="tooltip" {...rest}>
        {children}
      </TooltipPrimitive.Root>
    </TooltipProvider>
  );
}

function TooltipTrigger({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" type="button" {...props} />;
}

const TOOLTIP_VARIANT_CLASSES: Record<TooltipVariant, { content: string; arrow: string }> = {
  primary: {
    content: 'bg-gray-15 text-white',
    arrow: 'bg-gray-15 fill-gray-15',
  },
  secondary: {
    content: 'bg-white text-text-primary border border-border-default shadow-sm',
    arrow: 'bg-white fill-white',
  },
};

function TooltipContent({
  className,
  arrowClassName,
  sideOffset = 0,
  withinPortal = false,
  variant = 'primary',
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content> & {
  withinPortal?: boolean;
  variant?: TooltipVariant;
  arrowClassName?: string;
}) {
  const variantClasses = TOOLTIP_VARIANT_CLASSES[variant];

  // z-[900]: a portalled tooltip has to clear every overlay its trigger can sit in — dialogs (400s),
  // raised popovers (500s), the bulk upload modal stack (700s), editor toolbars (800) — or it opens
  // hidden beneath it.
  let content = (
    <TooltipPrimitive.Content
      data-slot="tooltip-content"
      sideOffset={sideOffset}
      className={cn(
        'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-[900] w-fit origin-(--radix-tooltip-content-transform-origin) rounded-md px-3 py-1.5 text-xs font-medium',
        variantClasses.content,
        className,
      )}
      collisionPadding={16}
      {...props}
    >
      {children}
      <TooltipPrimitive.Arrow
        className={cn(
          'z-[900] size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]',
          variantClasses.arrow,
          arrowClassName,
        )}
      />
    </TooltipPrimitive.Content>
  );

  if (withinPortal) content = <TooltipPrimitive.Portal>{content}</TooltipPrimitive.Portal>;

  return content;
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
